import { useState } from 'react'
import { Link } from 'react-router-dom'

import Disclosure from './Disclosure.jsx'
import Person, { People } from './Person.jsx'
import TrackTitle from './TrackTitle.jsx'
import {
  MAX_QUERY_CHARS,
  PROBING,
  RESULTS,
  stateSentence,
  useMusicControls,
} from './controls.js'
import { freshness, shortDate } from './freshness.js'
import {
  LOADING,
  MISCONFIGURED,
  READY,
  artworkUrl,
  canonOf,
  canonSize,
  chartPeriod,
  chartRows,
  idsWereDropped,
  inductedBy,
  nowPlayingOf,
  queueOf,
  requesterId,
  runtime,
  trackArtist,
  trackResolved,
  useMusic,
  useTabVisible,
} from './music.js'

// The music panel — the window on the room (P6-186, M2) and, since P6-189 (M5),
// the room's second set of controls.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT WAS A WINDOW AND THE ABSENCE WAS THE DELIVERABLE. THAT PARAGRAPH IS
//    KEPT, IN THE PAST TENSE, BECAUSE ITS CONDITION IS WHAT RETIRED IT.
// ═══════════════════════════════════════════════════════════════════════════
//
// It read: *"Controls are M4 (the tunnel, behind ADR-0006 and three human gates)
// and M5 (the buttons). Until the tunnel exists there is nothing on the other
// end of a button... a greyed button saying 'music's box is offline' would be
// this page asserting an outage that has not happened."*
//
// ✅ EVERY CLAUSE OF THAT CAME TRUE IN ORDER. ADR-0006 was accepted 2026-09-04,
// the control API merged (P6-188, #429), the sidecar went live and the DNS route
// for `music.v3gapunk.day` landed the same day. There is now something on the
// other end of a button, and the reachability wording is no longer an assertion
// about an outage — it is the answer to a probe (`src/portal/controls.js`).
//
// 🔴 WHAT DID **NOT** RETIRE WITH IT, AND MUST NOT: `music/utils/projection.py`
// STILL WITHHOLDS `control` AND `last_commands` FROM THE BLOB. This panel's
// reachability comes from `/api/music/control`, its own route, and is never
// written back onto `shared` — `src/portal/music.js`'s `NOT_YET_PUBLISHED` is
// that rule as data and `music-window.test.jsx` still walks it. The absence in
// the projection and the presence of a button are two different facts.
//
// ⚠️ THE GATE THAT GUARDED THE ABSENCE WAS REVISED, NOT DELETED. It used to read
// *"every button inside the panel must carry `aria-expanded`"*. It now reads
// *"every button is a disclosure OR carries a `data-control` naming an op the
// service implements"* — so a button nobody reviewed still reds the build, which
// is the property the old assertion actually bought. Deleting a gate because it
// now fails is how a slice loses the thing the previous slice paid for.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 AND NO POSITION, NO PROGRESS BAR, NO ELAPSED TIME
// ═══════════════════════════════════════════════════════════════════════════
//
// `GuildPlayer` records no pause time. `Track.position()` is `now - started_at`
// and keeps advancing while paused, and `/pause` writes nothing — so **a paused
// track's position is not derivable from anything the service stores.** The
// publisher says so and publishes the two honest fields instead: the anchor
// (`started_at`) and the state.
//
// M3 owns that decision — teach the player to record pause time, or animate
// from the anchor and freeze on `paused`. Either way it is a decision, and a
// progress bar drawn here from `started_at` would pre-empt it with a number
// that is wrong the moment somebody presses pause and has nothing to log it.
//
// ⚠️ SO `started_at` IS PUBLISHED AND DELIBERATELY NOT RENDERED. It is M3's
// input. `duration_s` IS rendered, because a track's LENGTH is a fact about the
// track that does not move — see `runtime()` on why formatting the first is not
// a step towards the second.
//
// ⛔ `envelope_key` and `source_url` are likewise published and unrendered. The
// envelope is the visualizer's input (M3). The source URL is a link off this
// site to somebody else's player, which is an affordance rather than a fact
// about the room, and M2's list is *now playing, queue, canon, charts, as-of
// stamps*.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE TITLES ARE LOWER-CASE IN THE STORE, AND THE FIX IS CSS, NOT A STRING
// ═══════════════════════════════════════════════════════════════════════════
//
// `music/utils/identity.py::_tidy` lower-cases every resolved identity — it
// also strips accents and turns `&` into ` and `, because the point of the
// normaliser is a stable KEY and not a display name. `/recap` posts the result
// as it is, so the crew already reads `daft punk - one more time` in Discord.
//
// The transform below is `text-transform: capitalize` in `app.css`, applied to
// exactly two classes and only where the publisher's `resolved` flag says the
// string came out of the normaliser. Three properties make that the honest
// choice rather than a JS title-caser:
//
//   1. THE DOM TEXT IS UNCHANGED. A screen reader, a copy-paste and every
//      assertion in the test suite get the stored value. Nothing downstream is
//      handed a guess.
//   2. IT IS SCOPED BY A PUBLISHED FIELD. An UNRESOLVED row carries the
//      upload's own words in the uploader's own casing — somebody else's
//      string — and is left alone.
//   3. IT IS REVERSIBLE IN ONE RULE, which matters because it is a taste call
//      and not a measurement.
//
// ⚠️ ITS KNOWN IMPERFECTION, STATED RATHER THAN DISCOVERED: `capitalize` renders
// `dj shadow` as `Dj Shadow` and `r.e.m.` as `R.e.m.`. It is a DISPLAY
// CONVENTION, not a restoration — the normaliser's other losses (accents, the
// ampersand) are not recoverable here at all, and pretending otherwise in
// JavaScript would put a fabricated title into the document.
//
// ✅ THE RETIREMENT CONDITION, next to the compromise: this rule goes when the
// music service keeps the display form beside the key — a `display_title` on
// the identity record, written once by the normaliser that already has the
// original in `source_title`. That is a music-side change and it is not M2's to
// make. Until then the site is choosing how to draw a string, which is the only
// half of this it is entitled to decide.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 P6-207 — THE DESIGN PASS, AND THE TWO PLACES THE CANVAS COULD NOT BE
//    FOLLOWED LITERALLY
// ═══════════════════════════════════════════════════════════════════════════
//
// Jordan's brief: *"the music page is something people will have open on side
// screens a lot, it should be pleasant to have there."* The cover became the
// anchor (72px -> 420), the lists lost their chrome, pause/resume became the
// only filled control, and the band sits behind a wash taken from the cover.
//
// ⚠️ NOTHING ABOUT WHAT THE PANEL DOES CHANGED. Same ops, same outcomes, same
// live regions and ids, same absences — no elapsed time, no progress bar, no
// scrub position, because `position_at_event` is still unpublished and that is
// M3's decision. Nothing new is read off the blob: the wash is the SAME
// `artwork_url` the slot already drew.
//
// 🔴 TWO DIVERGENCES FROM THE CANVAS, REPORTED RATHER THAN QUIETLY REDEFINED:
//
//   1. **THE BAND STACKS; IT IS NOT SIDE BY SIDE.** The canvas draws a 420px
//      cover with the title in a second column on a 1240px artboard. Measured
//      in a browser, this panel is 532px on every desktop and 688px at its
//      widest — `.d2d-portal__columns` gives it half the page from ~800px up.
//      A two-column band needs ~730px of content box that the panel never has.
//      The full table is beside `.d2d-portal__nowband` in `app.css` and in
//      `NowPlaying` below.
//
//   2. **NO COVER MEANS NO WASH.** The busy artboard draws a coloured wash
//      beside the `no art` stripe, because the canvas approximates the wash
//      with a gradient and has no real image to derive one from. Here there is
//      nothing to derive: a colour invented for a track with no artwork would
//      be the panel asserting something it does not have, which is the same
//      rule that keeps `started_at` unrendered. The stripe is the designed
//      absence and it stands on the flat ground.

/** How many queue rows the panel shows before it starts counting instead.
 *
 * The phone is the reason, exactly as `MoviePanel`'s `RECENT` records: six rows
 * is a glance and `utils/player.QUEUE_CAP` is a hundred. */
const QUEUE_SHOWN = 6

/** How many canon entries and chart rows the panel shows. The publisher already
 * caps the canon at `RECENT_CANON` (20) and the charts at `CHART_LIMIT` (5);
 * this is the panel's own, smaller, glance. */
const CANON_SHOWN = 5

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 THE CONTROLS — AND THE ONE RULE THAT DECIDES EVERY BUTTON BELOW
// ═══════════════════════════════════════════════════════════════════════════
//
// ADR-0006 D4: *"The portal never gains a power Discord lacks."* Its enforcement
// is entirely on the service side, where `music/cogs/control.py` applies every
// precondition as the same member and lists the three places it is deliberately
// STRICTER than the slash commands.
//
// What that leaves this file is the OPPOSITE obligation, and it is the one that
// is easy to get wrong while feeling helpful: **do not disable a button to
// simulate a precondition.** Not "you are not in the voice channel", not "that
// track is not yours", not "nothing is playing" — the projection this panel
// draws from is up to two seconds old and carries no voice state at all, so
// every one of those would be a guess. The service answers each of them with a
// `200` and a label; a greyed button would replace a sentence somebody can read
// with a capability silently removed.
//
// ⚠️ THE TWO THINGS THAT DO DISABLE A BUTTON, AND NEITHER IS A GUESS:
//   1. The tunnel is not reachable — measured by asking, ADR-0006 D7.
//   2. A command is already in flight — `controls.js` explains why the panel
//      serialises rather than reordering optimistically.

/**
 * One control. A real `<button>`, and `data-control` names the op it sends.
 *
 * 🔴 `data-control` IS NOT DECORATION, IT IS HOW THE ABSENCE GATE SURVIVED
 * BECOMING A PRESENCE GATE. `music-window.test.jsx` used to require every button
 * in this panel to carry `aria-expanded`; it now requires every button to be a
 * disclosure OR to name an op, and asserts the set of ops against the ones the
 * spec's §5 table lists as live. A fourth button somebody adds without a
 * decision has neither, and reds the build.
 */
function Control({ op, label, onPress, disabled, describedBy }) {
  return (
    <button
      type="button"
      className="d2d-portal__control"
      data-control={op}
      disabled={disabled}
      aria-describedby={describedBy}
      onClick={onPress}
    >
      {label}
    </button>
  )
}

/**
 * Skip, like, and pause-or-resume, on the now-playing block.
 *
 * ⚠️ ONE BUTTON FOR PAUSE AND RESUME, CHOSEN BY THE PUBLISHED `state`. That is
 * not the panel guessing at a precondition: `state` is a fact the publisher
 * writes, and the two ops are opposites, so offering both at once would ask the
 * member to know which one applies. When the published state is stale the
 * service answers `already_paused` or `not_paused` and the sentence appears —
 * which is the same handling every other refusal gets.
 */
function NowPlayingControls({ state, controls, describedBy }) {
  const paused = state === 'paused'
  const disabled = controls.busy || !controls.live
  return (
    <div className="d2d-portal__controls" data-testid="now-playing-controls">
      <Control
        op="skip"
        // 🔴 THE LABEL SAYS **VOTE**, BECAUSE THAT IS WHAT IT DOES. ADR-0006 D4:
        // *"a skip is still a vote"*. A button labelled "Skip" would promise the
        // portal could do something `/skip` cannot, in one word, before anybody
        // pressed it — D4's falsifier arriving as a caption.
        label="Vote to skip"
        onPress={() => controls.send('skip')}
        disabled={disabled}
        describedBy={describedBy}
      />
      <Control
        op="like"
        label="Like"
        // 🔴 NO `key` ARGUMENT. The ❤️ button in Discord has no argument — it
        // likes what is playing — and `music/cogs/control.py` refuses a `key`
        // that is not the current track for exactly that reason. Sending the
        // key we happen to be rendering would race the projection and turn a
        // stale panel into a refusal for no gain.
        onPress={() => controls.send('like')}
        disabled={disabled}
        describedBy={describedBy}
      />
      <Control
        op={paused ? 'resume' : 'pause'}
        label={paused ? 'Resume' : 'Pause'}
        onPress={() => controls.send(paused ? 'resume' : 'pause')}
        disabled={disabled}
        describedBy={describedBy}
      />
    </div>
  )
}

/**
 * Move a queued track, or take it off.
 *
 * 🔴 BUTTONS RATHER THAN DRAGGING, AND THE SPEC'S §9.5 SAYS "DRAGGING" — so the
 * divergence is stated here rather than discovered. Three reasons, in order of
 * weight:
 *
 *   1. **The phone is the target.** P6-176 and P6-192 are two separate records of
 *      this panel being measured at the wrong width; a drag handle is a control
 *      that only works if you can hold the row still, on the surface where the
 *      row is 320px wide and the page scrolls under your thumb.
 *   2. **A drag is not operable from a keyboard**, and the HTML5 drag events are
 *      not fired by any assistive technology. `Disclosure.jsx` already carries
 *      this repo's ruling on the shape — *"a `<div onClick>` is not focusable,
 *      not operable from a keyboard, and announces nothing"*.
 *   3. `move` takes `{from, to}` and nothing about the op needs a gesture.
 *
 * ⚠️ SO §9.5's ACCEPTANCE IS MET BY REORDERING AND NOT BY DRAGGING, and that is
 * reported rather than quietly redefined. A drag affordance ON TOP of these
 * would be a genuine addition for a mouse; it is not built here.
 *
 * 🔴 POSITIONS ARE 1-BASED AND SENT AS THEY ARE PUBLISHED. `music/cogs/control.py`
 * bounds them before indexing — P6-194 is the record of what `/remove 0` does on
 * the Discord side, where it does not — so a position this panel gets wrong is
 * refused rather than applied to a different track.
 */
function TrackControls({ position, last, controls, describedBy }) {
  const disabled = controls.busy || !controls.live
  return (
    <span className="d2d-portal__trackacts" data-testid={`queue-actions-${position}`}>
      <Control
        op="move"
        label="Up"
        onPress={() => controls.send('move', { from: position, to: position - 1 })}
        // ⚠️ THE ONE PLACE A BUTTON IS DISABLED FOR A REASON THAT IS NOT THE
        // TUNNEL, AND IT IS NOT A PRECONDITION GUESS: there is no position 0.
        // The destination is arithmetic on a number the publisher sent, not a
        // claim about whose track it is or where anybody is standing.
        disabled={disabled || position <= 1}
        describedBy={describedBy}
      />
      <Control
        op="move"
        label="Down"
        onPress={() => controls.send('move', { from: position, to: position + 1 })}
        disabled={disabled || last}
        describedBy={describedBy}
      />
      <Control
        op="remove"
        label="Remove"
        onPress={() => controls.send('remove', { position })}
        // 🔴 NOT DISABLED FOR SOMEBODY ELSE'S TRACK, DELIBERATELY. The panel
        // knows `requester_id` and could grey this out — and it would be wrong
        // to. `music/cogs/control.py` answers *"That is not your track. Removing
        // someone else's needs a vote, which is not built yet"*, which is a
        // sentence that tells a member something; a greyed button tells them
        // nothing and looks like the site being broken. It is also the exact
        // field #426 made joinable and the panel refuses to guess at when it
        // arrives unusable — so the disable would silently vanish on a stale
        // blob.
        disabled={disabled}
        describedBy={describedBy}
      />
    </span>
  )
}

/**
 * What the tunnel is doing, in words, when it is doing anything worth saying.
 *
 * 🔴 `PROBING` RENDERS NOTHING. Nothing has failed yet, and a panel that said
 * "music's box is offline" for the first moment of every load would report an
 * outage that has not happened — on a POLLED surface, every time somebody opened
 * the pill. `music.js` already made this call for the projection and the
 * reasoning is the same one.
 */
function ControlState({ state }) {
  const sentence = stateSentence(state)
  if (state === PROBING || sentence === null) return null
  return (
    <p className="d2d-portal__flat" data-testid="control-state" data-control-state={state}>
      {sentence}
    </p>
  )
}

/**
 * The answer to the last thing somebody pressed.
 *
 * 🔴 A REFUSAL IS RENDERED AS AN ANSWER, NOT AS AN ERROR. Spec §5: *"the member
 * did nothing wrong."* So there is one element, one voice, and no styling that
 * separates `refused: not in voice` from `skipped` — CLAUDE.md's Register says
 * plain language belongs in anything about somebody's picks, and a red box round
 * "Join the voice channel to control the music" would be the personality that
 * section forbids in exactly the place it forbids it.
 *
 * ⚠️ `aria-live="polite"` BECAUSE THE OUTCOME IS THE ONLY FEEDBACK A BUTTON
 * GIVES. The queue itself updates on the projection's own two-second poll, so a
 * member using a screen reader would otherwise press "Vote to skip" and hear
 * nothing at all until the panel happened to redraw.
 */
function ControlOutcome({ id, result, onRetry }) {
  return (
    <div className="d2d-portal__outcome" id={id} aria-live="polite" data-testid="control-outcome">
      {result === null ? null : (
        <>
          <p className="d2d-portal__flat" data-outcome={result.outcome ?? result.state}>
            {result.message}
          </p>
          {result.retryable ? (
            // 🔴 THE RETRY REUSES THE COMMAND ID, AND THAT IS THE WHOLE REASON
            // THIS BUTTON IS HERE RATHER THAN "PRESS IT AGAIN". Spec §5: *"a
            // repeated `id` returns the original outcome"*. A member pressing
            // the original button again mints a NEW id, which for `skip` is a
            // second vote from one intention. This one cannot do that.
            <button
              type="button"
              className="d2d-portal__control"
              data-control="retry"
              onClick={onRetry}
            >
              Try that again
            </button>
          ) : null}
        </>
      )}
    </div>
  )
}

/**
 * The search box, and the five candidates it comes back with. P6-199 (M5).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 IT OFFERS WHAT `/play` OFFERS AND STOPS WHERE `/play` STOPS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ADR-0006 D4, and the shape is copied from the slash command rather than
 * designed here: type words → **five** candidates (title · channel · duration)
 * → tap one → it is queued as you. `music/cogs/control.py` answers both halves
 * through `utils/resolver.py`, which is MS4's *one resolver, two callers*, so
 * the menu in Discord and the list below are two renderings of one list.
 *
 * ⚠️ THE PICK SENDS `enqueue {url}`, WHICH IS WHAT DISCORD'S PICK DOES.
 * `MusicCog.play_candidate` resolves `candidate.url` and enqueues it; there is
 * no second path, and in particular there is no `enqueue {key}` — the service
 * refuses that form and says why.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 WHAT IS **NOT** OFFERED HERE, STATED SO IT IS A DECISION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `/play` takes a URL as well as words: `classify_query` sends a link straight
 * to extraction. This box sends everything as `search`, so a pasted link is
 * searched FOR rather than played. That is the portal being **weaker** than
 * Discord, which D4 permits and D4's falsifier is not about — and it is
 * deliberate rather than missed: classifying here would be a second
 * implementation of `classify_query` in another language, which is CLAUDE.md
 * gotcha 21's exact disease (a guard and its consumer, two matchers for one
 * contract, disagreeing by a case fold). Reported rather than papered over.
 *
 * ⚠️ NO URL IS RENDERED. The panel's header already settled this for
 * `source_url` — *"a link off this site to somebody else's player, which is an
 * affordance rather than a fact about the room"* — and the same holds for a
 * candidate's. The url is carried as the value that goes back, and never drawn.
 */
function SearchBox({ controls, answer, results, answerId }) {
  const [query, setQuery] = useState('')
  const typed = query.trim()
  const disabled = controls.busy || !controls.live

  const submit = (event) => {
    event.preventDefault()
    // 🔴 THE COMMAND IS MINTED HERE, ON THE PRESS. `sendControl` defaults its
    // id at call time and the Function stamps `issued_at` when the request is
    // built — which matters because `music/utils/control.py` refuses anything
    // older than thirty seconds as `stale`. A box that minted either on focus
    // would go stale while somebody typed, and the member would be told their
    // request was too old to apply for the crime of thinking about it.
    if (typed === '') return
    controls.send('search', { query: typed })
  }

  return (
    <div className="d2d-portal__block" data-testid="music-search">
      {/* 🔴 THE `<label>` IS THE BLOCK'S HEADING, AND P6-207 DELETED THE
          DECORATIVE `<span>` THAT USED TO SIT ABOVE IT. Every other block here
          carries a `d2d-portal__blocklabel`, so this one had one too — and it
          said "Search" directly above a `<label>` saying "Search for a track",
          which renders as two stacked mono labels for one box. The canvas draws
          one. The one that survives is the `<label>`, because it is the one
          doing work: it names the input.

          A real label, not a placeholder. `Disclosure.jsx` carries this repo's
          ruling on controls that announce nothing, and a placeholder disappears
          the moment somebody types — leaving a screen reader with an unnamed
          box and a sighted member with no reminder of what it searches. The
          text is unchanged, so the accessible name is unchanged; only the
          duplicate went. */}
      <form className="d2d-portal__searchform" onSubmit={submit}>
        <label className="d2d-portal__searchlabel d2d-micro" htmlFor="portal-music-search">
          Search for a track
        </label>
        <div className="d2d-portal__searchrow">
          <input
            id="portal-music-search"
            className="d2d-portal__searchinput"
            type="search"
            value={query}
            maxLength={MAX_QUERY_CHARS}
            autoComplete="off"
            onChange={(event) => setQuery(event.target.value)}
            disabled={disabled}
            aria-describedby={answerId}
          />
          <Control
            op="search"
            label="Search"
            onPress={submit}
            // ⚠️ THE SECOND PLACE A BUTTON IS DISABLED FOR SOMETHING THAT IS
            // NOT THE TUNNEL, AND LIKE `move`'s IT IS NOT A PRECONDITION
            // GUESS: it is a fact about the box. An empty query is a
            // guaranteed `400 query must be a non-empty string`, which
            // `sendControl` reads as `broken` — *"our bug"* — and it would be
            // ours. Nothing here claims to know anything about the room.
            disabled={disabled || typed === ''}
            describedBy={answerId}
          />
        </div>
      </form>
      <SearchAnswer id={answerId} answer={answer} results={results} controls={controls} />
    </div>
  )
}

/**
 * What came back: five rows, a sentence, or both.
 *
 * 🔴 THE SERVICE'S OWN SENTENCE IS SHOWN AS WRITTEN, and for a search there are
 * three of them because there are three answers: *"Pick one:"*, *"Nothing came
 * back for that…"*, and whatever `MEMBER_FACING` says when the extraction
 * failed. `utils/resolver.py` exists partly to keep those three apart — *"I
 * looked and found none"* is not *"I could not look"* — and a paraphrase here
 * would be a fourth vocabulary that starts drifting the day it is written.
 */
function SearchAnswer({ id, answer, results, controls }) {
  return (
    <div
      className="d2d-portal__searchanswer"
      id={id}
      aria-live="polite"
      data-testid="search-answer"
    >
      {answer === null ? null : (
        <p className="d2d-portal__flat" data-outcome={answer.outcome ?? answer.state}>
          {answer.message}
        </p>
      )}
      {answer !== null && answer.retryable ? (
        // The same affordance the outcome region carries, and for the same
        // reason: the retry reuses the command id. See `ControlOutcome`.
        <button
          type="button"
          className="d2d-portal__control"
          data-control="retry"
          onClick={controls.retry}
        >
          Try that again
        </button>
      ) : null}
      {results === null || results.length === 0 ? null : (
        <ul className="d2d-portal__tracklist" data-testid="search-results">
          {results.map((row, index) => (
            <li key={`${index}-${row.url}`}>
              <span className="d2d-portal__trackpos d2d-micro">{index + 1}</span>
              <span className="d2d-portal__trackmain">
                {/* NOT `TrackTitle`: that component reads the publisher's
                    `resolved` flag to decide whether the store's normaliser
                    produced the string. A search candidate never went through
                    the normaliser at all — it is the upload's own words — so
                    it is drawn as somebody else's string, untransformed, which
                    is what `data-resolved="false"` already means one block up. */}
                <span className="d2d-portal__tracktitle" data-resolved="false">
                  {row.title}
                </span>
                {row.channel === null ? null : (
                  <span className="d2d-portal__trackmeta d2d-micro">{row.channel}</span>
                )}
              </span>
              {/* Only when there is one. `TrackRow` makes the same call one
                  block down: a candidate whose duration yt-dlp did not report
                  gets a shorter row rather than an empty column. */}
              {runtime(row.duration_s) === null ? null : (
                <span className="d2d-portal__trackaside d2d-micro">
                  {runtime(row.duration_s)}
                </span>
              )}
              <span className="d2d-portal__trackacts">
                <Control
                  op="enqueue"
                  label="Queue"
                  onPress={() => controls.send('enqueue', { url: row.url })}
                  disabled={controls.busy || !controls.live}
                />
              </span>
            </li>
          ))}
        </ul>
      )}
      {results !== null && results.length === 0 && answer?.outcome === RESULTS ? (
        // 🔴 THE SERVICE SAID *"here are your results"* AND NONE OF THEM COULD
        // BE READ. That is not "nothing came back" — the service has its own
        // word and its own sentence for that, `no_results`, and it arrives with
        // an empty list too. This branch is the one where the two disagree,
        // which means the wire mangled something, and the honest thing is to
        // say we could not read them rather than to render an empty box that
        // looks like an answer.
        <p className="d2d-portal__flat" data-testid="search-unreadable">
          We couldn’t read those results. Try again, or search in Discord.
        </p>
      ) : null}
    </div>
  )
}

/**
 * The playing track's artwork — the real image, or the designed absence. P6-202.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 THE SLOT IS DRAWN WHETHER OR NOT THERE IS AN IMAGE, AND THAT IS THE ROW'S
 *    FIRST HAZARD ANSWERED STRUCTURALLY RATHER THAN BY CARE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * *"A missing thumbnail must not become a broken image, and must not become a
 * layout jump either."* A slot that appears only when there is art is a jump by
 * construction — the title reflows the moment a track without any starts — and
 * a 404 arriving three seconds after the panel drew would move it a second
 * time. So the box is always there and only its CONTENTS change, which is
 * exactly what `MoviePanel.jsx`'s `PosterSlot` does and for the same reason.
 *
 * 🔴 A 404 LANDS IN THE SAME STATE AS "THERE NEVER WAS ONE", DELIBERATELY.
 * `src/trips/EntryCard.jsx` records this repo's ruling for a rotting CDN URL,
 * and it binds harder here: a YouTube thumbnail dies with its upload, and the
 * canon is meant to outlive uploads. *"There is no artwork to show"* is one
 * fact however it came about; a broken-image glyph is the only other option and
 * it is a second sentence about somebody else's CDN.
 *
 * ⚠️ SQUARE, AND `object-fit: cover`, SO THE SLOT DECIDES THE LAYOUT AND THE
 * IMAGE NEVER DOES. A YouTube thumbnail is 16:9, SoundCloud's artwork is 1:1,
 * and a Bandcamp one is neither reliably — there is no ratio to honour, so an
 * `aspect-ratio` on the box plus `cover` is the only shape that cannot be
 * pushed around by a file nobody chose. `PosterSlot` makes the same call for
 * TMDb's 2:3, where the ratio IS known.
 *
 * 🔴 AND HOTLINKED, RULED BY JORDAN 2026-09-05 — the third instance of a
 * settled pattern, not a new decision. `referrerPolicy="no-referrer"` is on it
 * for the reason the trips card's test already gives in its own name: *a CDN is
 * not told which page is looking*. The rejected option was an R2 cache, and the
 * reason is worth keeping — **a cache is a write path**, needing an owner under
 * principle 2, a staleness rule and a growth story for a jukebox that
 * accumulates one image per track ever played.
 *
 * ✅ RETIREMENT CONDITION, NEXT TO THE CODE (gotcha 6's pattern): everybody who
 * can see this page is a signed-in server member, and `music/cogs/music.py`
 * already renders the same image to the same people in the channel — so today
 * the hotlink tells that CDN nothing Discord has not. **If the portal ever
 * serves anyone who is not a server member, this gets revisited.**
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 IT REMEMBERS *WHICH* URL BROKE, NOT *THAT ONE DID* — AND THAT IS A REAL
 *    DIVERGENCE FROM `PosterSlot`, NOT A STYLISTIC ONE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `PosterSlot` holds a boolean, which is correct there: a movie night's poster
 * does not change while somebody is looking at it. **This panel polls every two
 * seconds and the track underneath it changes** — so a boolean set by one dead
 * URL would keep the slot empty for every track after it, for as long as the
 * pill stayed open. The member would see the first broken image forever, and
 * the page would look like a feature that simply stopped working.
 *
 * Storing the URL means the state is *about the thing that failed*, so a new
 * track is tried on its own merits and the slot heals by itself. It is also the
 * only version that a test can distinguish from the boolean: `music-window`
 * drives a failing image, then advances the projection, and asserts the second
 * track's art is drawn.
 *
 * ⚠️ THE STATE MOVED UP TO `NowPlaying` IN P6-207 AND THE REASON IS THE WASH.
 * The band behind this slot is the SAME image, blurred — so the art and the wash
 * have to agree about whether that URL is alive. Two `useState`s would be two
 * answers to one question, and the failure would be a blurred ghost of a picture
 * the slot had already given up on. One owner, two renderings; the property this
 * block describes is unchanged, it just lives one component up.
 */
function ArtSlot({ src, onBroken }) {
  return (
    <span className="d2d-portal__art" data-art={src ? 'true' : 'false'}>
      {src ? (
        <img
          className="d2d-portal__artimg"
          src={src}
          // 🔴 EMPTY ALT. The track's title and artist are the very next thing
          // in the document and are real text; a screen reader announcing
          // "image, one more time" would say the same sentence twice. It is
          // decoration beside its own caption — `PosterSlot` and the trips card
          // both landed here, and this is the third.
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          onError={onBroken}
          data-testid="now-playing-art"
        />
      ) : (
        <span className="d2d-micro" data-testid="now-playing-art-absent">
          no art
        </span>
      )}
    </span>
  )
}

/**
 * The cover, again, blurred out behind the whole band. P6-207.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 THE IMAGE ITSELF, NOT A COLOUR SAMPLED FROM IT — AND THE OBVIOUS
 *    IMPLEMENTATION THROWS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The reflex is to draw the cover into a `<canvas>` and read an average colour
 * out of it. That cannot work here: the artwork is HOTLINKED from somebody
 * else's CDN with no `Access-Control-Allow-Origin`, so the canvas is tainted the
 * moment it is drawn and `getImageData` raises a `SecurityError`. `crossOrigin`
 * would not rescue it either — it would make the request itself fail, and the
 * ART slot above would lose its picture to buy a colour for the wash.
 *
 * ✅ So the wash IS the image: same `src`, blurred, dimmed, under a scrim. No
 * pixel is ever read, so there is no CORS surface at all, and the browser serves
 * it from the same cache entry the slot already filled — one request, two
 * renderings.
 *
 * 🔴 AN `<img>` AND NOT A `background-image`, AND THAT IS A SAFETY DECISION.
 * `artworkUrl` returns the publisher's RAW string (it validates the parse and
 * the scheme, then hands back `raw`, not `parsed.href`), so the value can still
 * contain a quote or a paren. Interpolating it into `url("…")` puts somebody
 * else's string into a CSS value; an `src` attribute is escaped by React and
 * parsed as a URL by the browser, which is the boundary that already exists.
 *
 * ⚠️ `aria-hidden` ON THE WRAPPER AND AN EMPTY `alt` ON THE IMAGE. It is the
 * same picture as the slot, so announcing it would say one thing twice — and it
 * is 64px of blur behind a scrim, which is not a picture anybody is looking at.
 * It is also `pointer-events: none` in CSS: nothing in this layer is clickable
 * and it covers the whole band.
 */
function Wash({ src, onBroken }) {
  // 🔴 P6-211. THE GLOW'S LOOP STOPS WHEN NOBODY IS LOOKING, ON THE SAME SIGNAL
  // THE POLL ALREADY USES. `useMusic` tears its interval down on
  // `visibilitychange`; a perpetual compositor animation behind a hidden tab is
  // the same waste in a different currency, and this panel is built to be left
  // open on a side screen all day. One seam, one answer — see `useTabVisible`.
  //
  // ⚠️ THE HOOK IS HERE RATHER THAN IN `NowPlaying`, so the concern belongs to
  // the layer that has it and nothing has to be drilled through a component
  // that does not care. `Wash` is only ever rendered from the branch where a
  // track exists, and hooks in a component are unconditional within it.
  const drifting = useTabVisible()
  // 🔴 P6-224. ONE EXPRESSION, WRITTEN ONTO TWO WRAPPERS. The glow and the wash
  // are separate elements now — the glow is outside the band's clip — so each
  // needs its own pause hook. Deriving the string ONCE is what makes "one answer
  // to `is anybody watching`" structurally true rather than remembered: two
  // `drifting ? … : …` expressions would be two answers that could drift apart
  // under a later edit, which is the shape of the sibling bug in CLAUDE.md's
  // principle 2.
  const drift = drifting ? 'on' : 'off'
  return (
    <>
      {/* ═══════════════════════════════════════════════════════════════════
          🔴 P6-224. THE HALO, AND IT IS OUTSIDE THE BAND'S CLIP ON PURPOSE
          ═══════════════════════════════════════════════════════════════════

          Jordan asked for a glow AROUND the music frame; what P6-211 shipped
          lived inside `.d2d-portal__nowwash`, which clips, so it only ever lit
          the band from within. This is the same `<img>` — not a fourth copy of
          the cover — lifted out of that wrapper and dropped BEHIND the panel's
          opaque surface with `z-index: -1`, so the only part of it that can be
          seen is the part that escapes the panel's outline.

          🔴 THE WRAPPER'S CLIP IS FOR THE DRIFT, NOT FOR THE BLUR, AND THAT IS
          THE OPPOSITE OF THE WASH WRAPPER ONE ELEMENT DOWN. Measured against a
          control that does overflow: a blurred element's INK never moves
          `documentElement.scrollWidth`, but `d2d-drift-a`'s `scale(1.14)` moves
          the BOX — 40px past the viewport at 1280 and 24px at 375, which is a
          red `Site Suite` and, under CLAUDE.md gotcha 14, an abandoned bot
          deploy. So the wrapper clips the moving box and carries the filter
          itself, which puts the blur OUTSIDE the clip where its ink can still
          reach past the frame. `app.css` carries the measurements. */}
      {src === null ? null : (
        <span className="d2d-portal__nowglow" aria-hidden="true" data-drift={drift}>
          <img
            className="d2d-portal__nowglowimg"
            src={src}
            alt=""
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={onBroken}
            data-testid="now-playing-glow"
          />
        </span>
      )}
      {/* 🔴 THE CLIP IS ON THIS WRAPPER AND **NOT** ON THE BAND, WHICH IS
          P6-176 ANSWERED STRUCTURALLY RATHER THAN BY REMEMBERING. A 64px blur
          spreads past its own box, so something has to clip it — and
          `overflow: hidden` on the band would make every element inside the
          band invisible to `documentElement.scrollWidth`, which is the exact
          blindness P6-176 records costing three surviving mutants on the movie
          panel.

          This wrapper is absolutely positioned, carries no layout content, and
          holds only the decorative layers. Clipping it contains the blur and
          leaves the band's real content in normal flow, where the viewport gate
          can still see it. `app.css` carries the same note next to the rule. */}
      <span className="d2d-portal__nowwash" aria-hidden="true" data-drift={drift}>
        {/* ⚠️ NO COVER, NO GLOW — and the same `src === null` guard stands over
            the halo above. The two conditionals were one before P6-224 split
            the layers across two wrappers; they are the same rule written
            twice, from the same `src`, and neither may outlive the other. A
            track with no artwork gets the flat ground, because there is no
            colour to be had and inventing one would be the panel claiming to
            know something about a record it cannot see. */}
        {src === null ? null : (
          <img
            className="d2d-portal__nowwashimg"
            src={src}
            alt=""
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            // The same handler the slot gets. A URL that 404s takes the wash with
            // it rather than leaving a broken-image glyph blurred behind the
            // title, and both layers land on the flat ground together.
            onError={onBroken}
            data-testid="now-playing-wash"
          />
        )}
        {/* 🔴 THE SCRIM IS WHAT MAKES THE TITLE LEGIBLE OVER A COVER NOBODY
          CHOSE, AND IT IS MEASURED RATHER THAN MODELLED. The worst case is a
          pure white cover — the blur makes it uniformly white — and `app.css`
          carries the composite and the contrast ratios it produces for every
          ink used on this band.

          ⚠️ SINCE P6-211 IT IS NOT ONE VALUE. It is heavier than the old flat
          0.60 wherever ink sits and lighter wherever none does, in two regimes,
          because the ink starts at 28-40% of the band when the cover is above
          the text and at 4.3% when it is beside it. Do not read a single number
          out of that rule, and do not compute one: `viewport.test.js` reads the
          brightest pixel behind every glyph, at every phase of the drift. */}
        <span className="d2d-portal__nowscrim" />
      </span>
    </>
  )
}

/**
 * What is playing, or the fact that nothing is — and, since P6-207, the band
 * that the rest of the panel is quiet around.
 *
 * 🔴 `now_playing: null` IS A MEASURED ANSWER, NOT A GAP. `capture_room` returns
 * it when nothing has played in this guild since boot, and the publisher's own
 * note draws the line: that *"is a real observation"*, and it is not the same
 * as the music cog being unreachable — which is `RoomUnobservable` and never
 * reaches a blob at all. So this renders a sentence rather than an empty box or
 * a spinner that never resolves.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 THE CONTROL SENTENCES MOVED IN HERE, AND IT IS THE SAME ARGUMENT THE
 *    SEARCH BOX ALREADY WON
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `MusicPanel` already routes a search's answer to the search box, because *"a
 * member who tapped 'Queue' on the fourth row and had the sentence appear four
 * blocks up would reasonably read it as being about that track"*. The same holds
 * for the other three ops: the answer to "Vote to skip" belongs beside the
 * button that sent it. Both live regions keep their ids, their `aria-live` and
 * their `aria-describedby` wiring — only the box they are drawn in moved.
 */
function NowPlaying({
  track,
  state,
  directory,
  controls,
  outcomeId,
  controlState,
  outcome,
  onRetry,
}) {
  // 🔴 ONE OWNER FOR "IS THIS URL ALIVE", TWO RENDERINGS OF IT. Hoisted out of
  // `ArtSlot` by P6-207 so the wash and the slot cannot disagree — see the block
  // above `ArtSlot` for why the state is a URL and not a boolean, which is
  // unchanged. The hook is called before the early return below, unconditionally.
  const src = artworkUrl(track)
  const [brokenSrc, setBrokenSrc] = useState(null)
  const art = src !== null && src !== brokenSrc ? src : null
  const onBroken = () => setBrokenSrc(src)

  if (track === null) {
    return (
      // ⚠️ THE QUIET ROOM GETS THE BAND WITHOUT THE PICTURE, NOT A DIFFERENT
      // BOX. There is no track, so there is no cover and no wash — but the panel
      // must not change shape when one starts, which is `ArtSlot`'s own argument
      // (*"a slot that appears only when there is art is a jump by
      // construction"*) applied one level out. The band keeps its ground and its
      // padding; only its contents are the sentence instead of a title.
      <div className="d2d-portal__nowband" data-testid="nothing-playing">
        <div className="d2d-portal__nowinner">
          <div className="d2d-portal__nowtext">
            <span className="d2d-portal__nowlabel d2d-micro" data-state="idle">
              Now playing
            </span>
            <p className="d2d-portal__flat">Nothing is playing right now.</p>
            {/* 🔴 THE CONTROLS ARE DRAWN HERE TOO, AND THAT IS D4 RATHER THAN AN
                OVERSIGHT. `now_playing: null` is a MEASURED answer —
                `capture_room` returns it when nothing has played since boot —
                and it is up to two seconds old. Hiding the buttons on it would
                be the panel deciding "nothing is playing, so there is nothing to
                skip", which is a precondition the service checks and answers
                (`nothing_playing`, with a sentence). The pause/resume label
                follows the published state, which for a quiet room is absent, so
                it reads "Pause". */}
            <ControlState state={controlState} />
            {controls === null ? null : (
              <NowPlayingControls state={undefined} controls={controls} describedBy={outcomeId} />
            )}
            <ControlOutcome id={outcomeId} result={outcome} onRetry={onRetry} />
          </div>
        </div>
      </div>
    )
  }

  const artist = trackArtist(track)
  const resolved = trackResolved(track)
  const length = runtime(track.duration_s)
  // `null` for an id that did not arrive as a string — see music.js. The line
  // shortens rather than naming the wrong person.
  const requester = requesterId(track)
  // 🔴 THE STATE WORD COMES OFF THE BLOB AND HAS NO THIRD SPELLING. The
  // publisher writes `playing` or `paused` and nothing else; anything we do not
  // recognise loses the qualifier rather than being printed raw, which is the
  // same rule `chartPeriod` follows one file over.
  const label = state === 'paused' ? 'Paused' : state === 'playing' ? 'Now playing' : 'In the room'

  return (
    <div className="d2d-portal__nowband" data-testid="now-playing">
      <Wash src={art} onBroken={onBroken} />
      {/* 🔴 THE ART IS THE ANCHOR AND THE TEXT IS UNDER IT, NOT BESIDE IT — AND
          THAT IS A MEASUREMENT, NOT A PREFERENCE. The canvas draws a 420px cover
          with the title in a second column, on a 1240px artboard. **The music
          panel is never 1240px wide.** Measured in a real browser, portal open,
          music pill selected:

              viewport   320   375   700   768   800   900  1024  1280+
              panel      280   335   660   688   352   402   464   532

          `.d2d-portal__columns` is `auto-fit minmax(340px, 1fr)`, so the panel
          takes HALF the page from ~800px up — it is at its widest (688) at
          viewport 768 and settles at **532** on every real desktop. A 420px
          column plus a gap leaves ~64px for a 72px title there, which is not a
          layout, and a two-column band would need ~730px of content box that
          this panel never has.

          ⚠️ So the phone artboard's stacking is the one that survives contact
          with the container, at every width, and it delivers the row's actual
          instruction better than a cramped two-column would: the cover goes from
          72px to 420 on desktop and 295 at 375. What is NOT delivered is the
          side-by-side composition — reported rather than quietly redefined. */}
      <div className="d2d-portal__nowinner">
        <ArtSlot src={art} onBroken={onBroken} />
        <div className="d2d-portal__nowtext">
          {/* The dot beside this label is a CSS `::before` keyed on `data-state`
              and not an element, so `textContent` is still exactly the word the
              publisher's state maps to — which is what `music-window` asserts
              and what a screen reader reads. A decorative dot that announced
              "playing" beside the word "Now playing" would say it twice. */}
          <span
            className="d2d-portal__nowlabel d2d-micro"
            data-testid="now-playing-state"
            data-state={state === 'paused' ? 'paused' : state === 'playing' ? 'playing' : 'unknown'}
          >
            {label}
          </span>
          <TrackTitle track={track} className="d2d-portal__nowtitle" />
          {artist === null ? null : (
            <span className="d2d-portal__nowartist" data-resolved={resolved ? 'true' : 'false'}>
              {artist}
            </span>
          )}
          {/* The meta line is assembled from the parts that exist, so a track
              missing one reads as a shorter sentence rather than as "undefined ·
              3:42" — `MoviePanel`'s VerdictBlock records the same shape. */}
          {requester === null && length === null ? null : (
            <p className="d2d-portal__meta">
              {requester === null ? null : (
                <>
                  Queued by <Person directory={directory} userId={requester} />
                </>
              )}
              {requester !== null && length ? ' · ' : null}
              {length ?? null}
            </p>
          )}
          {resolved ? null : (
            // The third outcome, said out loud. `identity.py`: an unresolved
            // identity is *"not an error path, it is a first-class result"*, it
            // is excluded from every chart, and staying countable is the whole
            // point of it. A member seeing their track absent from the charts
            // deserves to know why on the row itself.
            <p className="d2d-portal__flat" data-testid="now-unresolved">
              We could not read an artist and a title off this one, so it does not
              count towards the charts or the canon.
            </p>
          )}
          <ControlState state={controlState} />
          {controls === null ? null : (
            <NowPlayingControls state={state} controls={controls} describedBy={outcomeId} />
          )}
          <ControlOutcome id={outcomeId} result={outcome} onRetry={onRetry} />
        </div>
      </div>
    </div>
  )
}

/** One row of a track list. Position, name, and whatever else is published.
 *
 * ⚠️ P6-207: A ROW WITH NO POSITION LOSES THE COLUMN RATHER THAN FILLING IT.
 * This drew `·` where a number would go, which is a placeholder holding a grid
 * column open for a thing that does not exist — the canon is not an ordered
 * list, and the canvas draws its rows as title-and-date with no numeral. The
 * dot read as a bullet nobody meant. The column is dropped by `data-numbered`
 * rather than by a second component, so one row markup still serves the queue,
 * the canon and both charts.
 */
function TrackRow({ track, directory, position, aside, actions = null }) {
  const artist = trackArtist(track)
  const requester = requesterId(track)
  // `== null` covers `null` and a missing prop. The queue passes the
  // publisher's `position`, which it already refuses to build controls from
  // when it is not an integer — so a malformed one loses its numeral here too,
  // which is the honest rendering of a row whose place we could not read.
  const numbered = position != null
  return (
    <li data-numbered={numbered ? 'true' : 'false'}>
      {numbered ? (
        <span className="d2d-portal__trackpos d2d-micro">{position}</span>
      ) : null}
      <span className="d2d-portal__trackmain">
        <TrackTitle track={track} className="d2d-portal__tracktitle" />
        {/* ⚠️ THE META LINE IS `d2d-micro`, WHICH UPPERCASES — so the artist is
            drawn as a LABEL here and the lower-case store value is invisible
            without any transform at all. That is why the capitalisation rule in
            the header covers the title and the now-playing artist and nothing
            else: those two are body text, and this is not. */}
        {artist === null && requester === null ? null : (
          <span className="d2d-portal__trackmeta d2d-micro">
            {artist ?? null}
            {artist && requester ? ' · ' : null}
            {requester ? (
              <>
                Queued by <Person directory={directory} userId={requester} />
              </>
            ) : null}
          </span>
        )}
      </span>
      {aside ? <span className="d2d-portal__trackaside d2d-micro">{aside}</span> : null}
      {/* ⚠️ THE ACTIONS SPAN THE WHOLE ROW AND SIT UNDER IT (`grid-column: 1/-1`
          in app.css), which is a 320px decision rather than a taste one. The row
          is already `2ch 1fr auto` with an ellipsising title; a fourth column of
          three buttons would compete with the title for the same pixels and the
          title is the thing somebody is reading. P6-192 is the record of what
          this panel does when a width is assumed rather than rendered. */}
      {actions}
    </li>
  )
}

/**
 * The live queue.
 *
 * 🔴 THE TOTAL IS IN THE LABEL AND THAT IS WHAT MAKES THE ROW'S ACCEPTANCE
 * TESTABLE. *"A member sees the queue move on the portal within a poll interval
 * of a `/play` in Discord."* `/play` APPENDS, so on a queue longer than
 * `QUEUE_SHOWN` the new track lands off the bottom of the list — a panel that
 * showed only the first six would poll correctly, update nothing visible, and
 * look exactly like a panel that was not polling at all. The count moves on
 * every enqueue whatever the length.
 *
 * ⚠️ AND IT IS NEVER A ZERO. An empty queue gets the sentence, not `0 tracks` —
 * P6-156's rule, and the publisher's own: *"a count is a claim; an absence is
 * not."* A count of nothing beside a list of nothing is the same fact twice,
 * and the numeral is the half that reads as a badge.
 */
function Queue({ queue, directory, open, onToggle, controls, outcomeId }) {
  const shown = open ? queue : queue.slice(0, QUEUE_SHOWN)
  const hidden = queue.length - shown.length
  // 🔴 `last` IS COMPUTED FROM THE WHOLE QUEUE, NEVER FROM `shown`. With the
  // disclosure closed the sixth visible row is not the last track, and a "Down"
  // disabled there would be the panel refusing a move the service would happily
  // apply — a power removed by a rendering decision, which is D4 pointing the
  // way nobody watches.
  const lastPosition = queue.length === 0 ? null : queue[queue.length - 1].position
  return (
    <div className="d2d-portal__block" data-testid="music-queue">
      <span className="d2d-portal__blocklabel d2d-micro">
        Up next
        {queue.length > 0 ? ` · ${queue.length} ${queue.length === 1 ? 'track' : 'tracks'}` : null}
      </span>
      {queue.length === 0 ? (
        <p className="d2d-portal__flat" data-testid="queue-empty">
          Nothing queued.
        </p>
      ) : (
        <>
          <ul className="d2d-portal__tracklist" data-testid="queue-list">
            {shown.map((track) => (
              <TrackRow
                key={`${track.position}-${track.key}`}
                track={track}
                directory={directory}
                position={track.position}
                aside={runtime(track.duration_s)}
                actions={
                  controls === null || !Number.isInteger(track.position) ? null : (
                    <TrackControls
                      position={track.position}
                      last={track.position === lastPosition}
                      controls={controls}
                      describedBy={outcomeId}
                    />
                  )
                }
              />
            ))}
          </ul>
          {hidden > 0 || open ? (
            <Disclosure
              open={open}
              onToggle={onToggle}
              className="d2d-portal__nightstoggle"
              data-testid="queue-toggle"
            >
              <span>{open ? 'Show fewer' : `${hidden} more`}</span>
            </Disclosure>
          ) : null}
        </>
      )}
    </div>
  )
}

/**
 * The canon — the earned playlist, with who earned it.
 *
 * 🔴 `inducted_by` IS 3a ON A DECIDED RULE, NOT AN OVERSIGHT. MI4: *a fact the
 * bot already posts to the whole server is 3a-eligible*, and the music spec's
 * §4.3 makes provenance the point — *"every entry can say who put it there"*.
 * An induction is announced to the room by the bot's own hand, so the ids here
 * carry nothing the crew cannot already see.
 *
 * ⚠️ THE SIZE COMES FROM `canon_size` AND NEVER FROM `canon.length`. The list is
 * capped by the publisher at `RECENT_CANON`, so its length is a property of the
 * cap rather than of the canon, and printing it as a total would be wrong by
 * however much history exists.
 */
function Canon({ canon, size, directory }) {
  const shown = canon.slice(0, CANON_SHOWN)
  // 🔴 ONLY IDS THE DIRECTORY CAN BE ASKED ABOUT. `inducted_by` arrives as JSON
  // NUMBERS and a real snowflake does not survive that — see music.js. An empty
  // list here is either "nobody is credited" or "we could not read who", and the
  // sentence is dropped either way rather than crediting the wrong member.
  const credited = shown.length === 0 ? [] : inductedBy(shown[0])
  return (
    <div className="d2d-portal__block" data-testid="music-canon">
      <span className="d2d-portal__blocklabel d2d-micro">
        The canon
        {size === null ? null : ` · ${size}`}
      </span>
      {shown.length === 0 ? (
        <p className="d2d-portal__flat" data-testid="canon-empty">
          Nothing has made the canon yet.
        </p>
      ) : (
        <ul className="d2d-portal__tracklist" data-testid="canon-list">
          {shown.map((entry) => (
            <TrackRow
              key={entry.key}
              track={{ ...entry, requester_id: null }}
              directory={directory}
              position={null}
              aside={shortDate(entry.inducted_at)}
            />
          ))}
        </ul>
      )}
      {credited.length === 0 ? null : (
        <p className="d2d-portal__meta" data-testid="canon-provenance">
          The newest one was put there by <People directory={directory} ids={credited} />.
        </p>
      )}
    </div>
  )
}

/**
 * The charts.
 *
 * ⛔ `most_skipped` COUNTS TRACKS AND NEVER PEOPLE, and no requester is drawn on
 * a chart row. The publisher withholds `skips[].voters` — *"the outcome is
 * public, the roll call is not"* — and `skips_against_you` is a 3b number that
 * belongs to its owner. A "most skipped" list with a name beside each row would
 * assemble the second thing out of the first.
 */
function Charts({ shared }) {
  const period = chartPeriod(shared)
  const top = chartRows(shared, 'top_played')
  const skipped = chartRows(shared, 'most_skipped')
  return (
    <div className="d2d-portal__block" data-testid="music-charts">
      <span className="d2d-portal__blocklabel d2d-micro">
        Charts
        {period === null ? null : ` · ${period}`}
      </span>
      {top.length === 0 && skipped.length === 0 ? (
        <p className="d2d-portal__flat" data-testid="charts-empty">
          Nothing has been played in this window yet.
        </p>
      ) : (
        <>
          <ChartList
            rows={top}
            label="Most played"
            testid="chart-top-played"
            empty="Nothing played in this window."
          />
          <ChartList
            rows={skipped}
            label="Most skipped"
            testid="chart-most-skipped"
            empty="Nothing was skipped in this window."
          />
        </>
      )}
    </div>
  )
}

function ChartList({ rows, label, testid, empty }) {
  return (
    <>
      {/* 🔴 A SUB-LABEL, BECAUSE "CHARTS · THIS MONTH" IS THE BLOCK AND THESE
          TWO ARE ITS HALVES. The canvas draws one labelled block containing
          both lists rather than two blocks, and the second level of label is a
          step quieter to say so — the alternative was two `Charts` headings on
          one panel, which reads as two measurements of two things. */}
      <span className="d2d-portal__sublabel d2d-micro">{label}</span>
      {rows.length === 0 ? (
        <p className="d2d-portal__flat">{empty}</p>
      ) : (
        <ul className="d2d-portal__tracklist" data-testid={testid}>
          {rows.map((row, index) => (
            <TrackRow
              key={row.key}
              track={{ ...row, requester_id: null }}
              directory={null}
              position={index + 1}
              // The play count, said as what it counts. A bare numeral in the
              // aside column would sit where a duration sits two blocks up.
              aside={Number.isInteger(row.count) ? `${row.count}×` : null}
            />
          ))}
        </ul>
      )}
    </>
  )
}

/**
 * The Music panel.
 *
 * 🔴 `useMusic` IS CALLED HERE AND NOT IN `PortalHome`, WHICH IS WHAT MAKES
 * "POLL WHILE THE PANEL IS VISIBLE" STRUCTURAL. The selector renders one panel
 * at a time, so switching pills unmounts this component and the hook's cleanup
 * clears the interval. A flag passed down from the page would be a second thing
 * that has to stay in step with which pill is open — see music.js.
 */
export default function MusicPanel({ directory, now, docPath }) {
  const music = useMusic()
  const controls = useMusicControls()
  const [queueOpen, setQueueOpen] = useState(false)

  const shared = music.state === READY ? music.shared : null
  const stamp = freshness(shared?.as_of, now)

  // 🔴 THE BUTTONS ARE DRAWN ONLY ONCE THE TUNNEL HAS ANSWERED, AND THE
  // ALTERNATIVE WAS WORSE IN BOTH DIRECTIONS. Drawing them disabled while the
  // probe is in flight is a control that looks broken for the first moment of
  // every load; drawing them enabled is offering something that may not be
  // there. `music.js` settled the same question for the projection — *"for the
  // first moment of every load NOTHING HAS FAILED"* — and the answer is to claim
  // nothing until something has answered.
  //
  // ⚠️ AND THEY ARE DRAWN WHEN THE TUNNEL IS DOWN, DISABLED, WITH THE SENTENCE
  // ABOVE THEM. ADR-0006 D7 asks for exactly that: *"The control buttons report
  // the tunnel as unreachable, plainly."* Removing them would make an outage
  // indistinguishable from the window it was before M5 shipped.
  const controlSurface = controls.state === PROBING ? null : controls
  const outcomeId = 'portal-music-outcome'

  // ═══════════════════════════════════════════════════════════════════════
  // 🔴 ONE ANSWER, TWO PLACES TO PUT IT — ROUTED BY THE OP THAT WAS SENT
  // ═══════════════════════════════════════════════════════════════════════
  //
  // `useMusicControls` holds exactly one `result`, because the panel sends
  // exactly one command at a time (see `controls.js` on why that is a
  // correctness decision and not a courtesy). P6-199 gives the panel a second
  // control surface, and an answer to a button in the search box belongs BESIDE
  // THAT BOX — a member who tapped "Queue" on the fourth row and had the
  // sentence appear four blocks up, next to the track that is playing, would
  // reasonably read it as being about that track.
  //
  // ⚠️ SO THE ROUTE IS THE OP, AND THE CONDITION UNDER WHICH THAT STOPS WORKING
  // IS WRITTEN HERE RATHER THAN LEFT TO BE DISCOVERED: `enqueue` is sent from
  // the search box and from nowhere else on this page. The day a second sender
  // appears — a "play this again" button on the canon, say — this must route on
  // the command's `id` instead, which the search box would then have to
  // remember. `music-controls.test.jsx` asserts the routing rather than the
  // mechanism, so that change is a rewrite of this constant and not of a test.
  const SEARCH_OPS = ['search', 'enqueue']
  const searchAnswer =
    controls.result && SEARCH_OPS.includes(controls.result.op) ? controls.result : null
  const controlAnswer = searchAnswer === null ? controls.result : null

  return (
    // 🔴 `d2d-portal__music` TURNS OFF THE PANEL'S OWN PADDING SO THE BAND CAN
    // REACH THE EDGES. It does NOT turn on `overflow: hidden` — the blur is
    // clipped by its own wrapper instead (see `Wash`), because a clip here would
    // hide every element in this panel from `documentElement.scrollWidth`, which
    // is P6-176's exact finding on the movie panel. `app.css` carries the rule
    // and the note; this class name is the seam between them.
    <section
      className="d2d-portal__panel d2d-portal__music"
      aria-labelledby="portal-music"
      data-testid="music-panel"
    >
      {/* ⚠️ THE BAND IS ABOVE THE HEADING IN THE DOCUMENT, WHICH THE CANVAS
          DRAWS AND WHICH COSTS NOTHING IN THE ACCESSIBILITY TREE: the section is
          `aria-labelledby` an id, and an id does not care where in the section it
          sits — the region still announces as "Music" on entry. Nothing else on
          the page points at the heading's position. */}
      {music.state === READY ? (
        <NowPlaying
          track={nowPlayingOf(shared)}
          state={nowPlayingOf(shared)?.state}
          directory={directory}
          controls={controlSurface}
          outcomeId={outcomeId}
          controlState={controls.state}
          outcome={controlAnswer}
          onRetry={controls.retry}
        />
      ) : null}

      <div className="d2d-portal__musicbody">
        <div className="d2d-portal__panelhead">
          {/* The heading shortens before the stamp does — the handoff's
              instruction after two measured 375px overflows. */}
          <h2 id="portal-music" className="d2d-portal__paneltitle d2d-micro">
            Music
          </h2>
          {music.state === LOADING ? null : (
            <span
              className="d2d-portal__stamp d2d-micro"
              title={stamp.title ?? undefined}
              data-testid="music-as-of"
            >
              {music.state === READY ? stamp.label : 'Unavailable'}
            </span>
          )}
        </div>

        {music.state === LOADING ? (
          // Nothing has failed yet, so nothing is claimed.
          <p className="d2d-portal__flat" data-testid="music-loading">
            Loading what’s playing…
          </p>
        ) : music.state === MISCONFIGURED ? (
          // 🔴 P6-200. NOT THE SAME SENTENCE AS THE ONE BELOW, AND THAT IS THE
          // WHOLE ROW. The site spent this release reading music's projection out
          // of the bot's bucket; `/api/music` answered "nothing published" forever
          // and the panel said *"unavailable right now"* — the sentence for a home
          // line being off. **A permanent misconfiguration is not a "right now",
          // and telling somebody to come back later when nobody coming back later
          // will fix it is the panel asserting something false.**
          //
          // ⚠️ IT NAMES NO BINDING AND NO BUCKET. A member cannot act on either,
          // and the operator's copy of this — which does name them — goes to the
          // log in `_lib/projections.js`. What a member needs is the true shape of
          // it: this is broken, it is not you, and it is not going to clear.
          <p className="d2d-portal__flat" data-testid="music-misconfigured">
            Music is set up wrong on this deployment, so it cannot be shown until that is fixed —
            this is not about your account. Everything else on this page is unaffected.
          </p>
        ) : music.state !== READY ? (
          <p className="d2d-portal__flat" data-testid="music-unavailable">
            Music is unavailable right now. Everything else on this page is unaffected.
          </p>
        ) : (
          <>
            {/* ONE stamp for the panel and not one per block. Spec §4 asks every
                block to carry an as-of; every block here comes out of ONE object
                with ONE `as_of`, so four copies of the same string would be noise
                claiming to be four measurements. `MoviePanel` makes the same call
                for the same reason.

                🔴 A DEDICATED GRID RATHER THAN `.d2d-portal__blocks`, BECAUSE
                THAT CLASS IS THE MOVIE PANEL'S TOO. It is `auto-fit
                minmax(230px, 1fr)`, which puts each block in its own cell — with
                four blocks and room for two columns that is a 2x2 of
                search|queue over canon|charts, and the canvas draws a LEFT STACK
                (search, up next, canon) beside ONE charts column. Restyling the
                shared class to get that would restyle `MoviePanel`, which is
                another lane's file. */}
            <div className="d2d-portal__musicblocks">
              <div className="d2d-portal__musiccol">
                {/* 🔴 ABOVE THE QUEUE, BECAUSE SEARCHING IS HOW SOMETHING GETS
                    INTO IT. The spec's §4 orders the WINDOW's blocks — now
                    playing, queue, canon, charts — and the search box is not one
                    of them; it is the panel's only way to change what those blocks
                    say, so it sits where the thing it feeds begins. */}
                {controlSurface === null ? null : (
                  <SearchBox
                    controls={controlSurface}
                    answer={searchAnswer}
                    results={searchAnswer?.results ?? null}
                    answerId="portal-music-search-answer"
                  />
                )}
                <Queue
                  queue={queueOf(shared)}
                  directory={directory}
                  open={queueOpen}
                  onToggle={() => setQueueOpen((was) => !was)}
                  controls={controlSurface}
                  outcomeId={outcomeId}
                />
                <Canon canon={canonOf(shared)} size={canonSize(shared)} directory={directory} />
              </div>
              <Charts shared={shared} />
            </div>

            {/* 🔴 A NAMED ABSENCE, NOT A SILENT ONE — and it disappears by itself.
                MI4 puts the queue's requester in 3a precisely so the panel can
                show it, and the ids do not survive JSON (music.js has the
                measurement). A member who saw "Queued by" simply missing would
                read it as the page being half-built, or as the member directory
                being down — which has its own note a little further down the page
                and is a different outage. This is derived from what arrived, so
                the day the music service publishes `str(...)` the names appear
                and this sentence stops rendering, with nothing here to edit. */}
            {idsWereDropped(shared) ? (
              <p className="d2d-portal__flat" data-testid="music-names-dropped">
                We can’t show who queued these yet.
              </p>
            ) : null}

            {/* 🔴 THIS SENTENCE WAS *"This is a window on the room. Play, skip and
                vote in Discord."* AND IT IS STILL THE RIGHT ONE WHEN THE TUNNEL IS
                DOWN — which is why it is kept rather than replaced. M2 wrote it to
                say what was true then and to *"make no claim about a tunnel, a
                reachability state, or a later slice"*. The later slice arrived, so
                the sentence now follows a MEASURED state instead of a fixed one.

                ⚠️ THE LIVE FORM SAYS WHAT THE BUTTONS DO, IN D4's OWN TERMS. A
                member who has just been shown three controls needs to know before
                pressing one that a skip is a vote and a like cannot be taken back
                — those are the two places the portal looks like it might be
                stronger than Discord and is deliberately not. `music/cogs/control.py`
                says the same thing after the fact, in an outcome; this says it
                first, which is cheaper for everybody. */}
            <p className="d2d-portal__flat" data-testid="music-window-note">
              {controls.live
                ? 'These do the same as the Discord commands: a skip is a vote, and a like is one per member.'
                : 'This is a window on the room. Play, skip and vote in Discord.'}
            </p>
          </>
        )}

        {docPath ? (
          <Link className="d2d-portal__more d2d-micro" to={docPath}>
            How music works →
          </Link>
        ) : null}
      </div>
    </section>
  )
}
